export const labels = {
  automatic: 'Automatic',
  calm: 'Calm',
  connected: 'Connected',
  inspired: 'Inspired',
  energized: 'Energized',
  latest: 'Latest',
};

export const toneLabels = {
  calm: 'A quiet moment',
  connected: 'Closer together',
  inspired: 'A little inspiration',
  energized: 'A spark of energy',
};

/** Keep the desktop menu transient without hiding a keyboard user's focused link.
 * Hidden links leave the tab order. Escape restores the launcher; navigation,
 * outside activation and leaving an unfocused menu dismiss it immediately. */
export function bindNavigationDock({ doc = globalThis.document, win = globalThis.window } = {}) {
  const rail = doc.querySelector('.left-rail');
  const nav = doc.querySelector('#navigation');
  const trigger = doc.querySelector('#navigation-toggle');
  if (!rail || !nav || !trigger) return () => {};
  let expanded = false;
  let hoverOpened = false;
  const setOpen = (open) => {
    expanded = open;
    if (!open) hoverOpened = false;
    rail.classList.toggle('navigation-open', open);
    nav.hidden = !open;
    trigger.setAttribute('aria-expanded', String(open));
  };
  const close = () => {
    if (nav.contains(doc.activeElement)) trigger.focus({ preventScroll: true });
    setOpen(false);
  };
  trigger.addEventListener('click', () => {
    setOpen(hoverOpened || !expanded);
    hoverOpened = false;
  });
  rail.addEventListener('pointerenter', (event) => {
    if (!expanded && event.pointerType === 'mouse' && win.matchMedia('(hover: hover)').matches) {
      setOpen(true);
      hoverOpened = true;
    }
  });
  rail.addEventListener('pointerleave', () => {
    if (!nav.contains(doc.activeElement)) setOpen(false);
  });
  rail.addEventListener('focusout', () => {
    queueMicrotask(() => {
      if (!rail.contains(doc.activeElement)) setOpen(false);
    });
  });
  doc.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && expanded) {
      event.preventDefault();
      close();
    }
  });
  nav.addEventListener('click', (event) => {
    if (event.target.closest('a')) close();
  });
  doc.addEventListener('pointerdown', (event) => {
    if (!rail.contains(event.target)) close();
  });
  win.addEventListener('hashchange', close);
  win.addEventListener('blur', close);
  doc.addEventListener('visibilitychange', () => {
    if (doc.hidden) close();
  });
  const narrow = win.matchMedia('(max-width: 720px)');
  narrow.addEventListener('change', close);
  setOpen(false);
  return close;
}

/** Synchronize shell presentation from current authorized state and explicit route. */
export function createShellNavigation(
  ctx,
  { applyShellLanguage, headerPreferences, modeWheel, ROUTE_FEATURES },
) {
  bindNavigationDock();
  return function shell() {
    const searchToggle = document.querySelector('#search-toggle');
    document.querySelector('#login').hidden = true;
    document.querySelector('#app').hidden = false;
    searchToggle.hidden = !ctx.featureEnabled('discovery');
    const preferences = document.querySelector('#top-preference-slot');
    preferences.hidden = false;
    const feeling = headerPreferences();
    if (preferences.firstChild !== feeling) preferences.replaceChildren(feeling);
    const modes = document.querySelector('#top-mode-slot');
    modes.hidden = false;
    const wheel = modeWheel();
    if (modes.firstChild !== wheel) modes.replaceChildren(wheel);
    document.querySelector('#mobile-nav').hidden = false;
    document.documentElement.dataset.theme =
      ctx.route()[0] === 'games' ||
      (['feed', 'learning'].includes(ctx.route()[0]) && ctx.experience === 'visual')
        ? 'dark'
        : ctx.state.settings.theme;
    applyShellLanguage();
    for (const item of document.querySelectorAll('#navigation a, #mobile-nav a')) {
      const key = ROUTE_FEATURES[item.dataset.route ?? item.hash.slice(2).split('/')[0]];
      item.hidden = !!key && !ctx.featureEnabled(key);
    }
    document.querySelector('#compose-nav').disabled = !ctx.featureEnabled('publishing');
    document.querySelector('#my-profile').href = `#/profile/${ctx.state.me.id}`;
    document.querySelector('#friend-count').textContent = ctx.state.friends.length;
    const unread =
      ctx.state.notificationCount ?? ctx.state.notifications.filter((n) => !n.read).length;
    document.querySelector('#notification-count').textContent = unread || '';
    const actions = document.querySelector('#header-actions');
    if (actions.children.length !== 1 || actions.firstChild !== searchToggle)
      actions.replaceChildren(searchToggle);
    document.querySelectorAll('#navigation a').forEach((a) => {
      const active = a.dataset.route === ctx.route()[0];
      a.classList.toggle('active', active);
      if (active) a.setAttribute('aria-current', 'page');
      else a.removeAttribute('aria-current');
    });
  };
}
